import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Modal, Box, Typography, makeStyles, Button, Stack, Snackbar,
} from '@material-ui/core';
import AddIcon from '@mui/icons-material/Add';
import moment from 'moment';
import { api_call, ucfirst } from '../helpers/untils';
import StripeCardModal from './StripeCardModal';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '480px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 3,
  outline: 'none',
};

const useStyles = makeStyles((theme) => ({
  title: {
    marginBottom: theme.spacing(2),
  },
  cardList: {
    maxHeight: 'calc(100vh - 260px)',
    overflowY: 'auto',
    paddingRight: theme.spacing(0.5),
  },
  cardRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: theme.spacing(1.5),
    border: '1.5px solid #e5e7eb',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    '&:hover': {
      border: '1.5px solid #2563eb',
      boxShadow: '0 0 0 3px rgba(37,99,235,0.08)',
    },
    marginBottom: theme.spacing(1),
  },
  cardRowSelected: {
    border: '1.5px solid #2563eb',
    boxShadow: '0 0 0 3px rgba(37,99,235,0.12)',
  },
  cardLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  cardRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    textAlign: 'right',
  },
  cardIcon: {
    width: '32px',
    height: 'auto',
  },
  nickname: {
    fontWeight: 500,
    fontSize: '14px',
    color: '#111827',
  },
  cardNumber: {
    fontWeight: 600,
  },
  holderNameEmpty: {
    fontSize: '13px',
    color: '#9ca3af',
  },
  expiry: {
    fontSize: '12px',
    color: '#6b7280',
  },
  empty: {
    color: '#6b7280',
    textAlign: 'center',
    padding: theme.spacing(3, 0),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  bottomActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1),
  },
}));

const CreditCardSelectModal = (props) => {
  const { open, setOpen, onSelect, onCancel, userId, currentPaymentMethodId, hideAdd = false } = props;
  const classes = useStyles();

  const [cards, setCards] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [addCardModalOpen, setAddCardModalOpen] = useState(false);

  const showToast = (msg) => {
    setToastMessage(msg);
    setSnackbarOpen(true);
  };

  const loadCards = async () => {
    setIsLoading(true);
    let url = `/api/payment/stripe/cards`
    if (userId) {
      url = `/api/payment/stripe/cards?userId=${userId}`
    }
    const response = await api_call(url);
    if (response.ok) {
      const res = await response.json();
      const list = res.cards || [];
      setCards(list);
      if (res.user) setUser(res.user);
      if (currentPaymentMethodId && list.some((c) => c.id === currentPaymentMethodId)) {
        setSelectedCardId(currentPaymentMethodId);
      }
    } else {
      setCards([]);
      showToast('Failed to load cards');
    }
    setIsLoading(false);
  };

  const onAddCardSaved = () => {
    setAddCardModalOpen(false);
    showToast('Credit card has been saved successfully!');
    loadCards();
  };

  useEffect(() => {
    if (open) {
      setSelectedCardId(currentPaymentMethodId || null);
      loadCards();
    }
  }, [open]);

  //////////////////////// app modal close feature //////////////////////////////////////////
  const appModalCloseTimestamp = useSelector((state) => state.layout.appModalCloseTimestamp);
  useEffect(() => {
    if (appModalCloseTimestamp) {
      setOpen(false)
    }
  }, [appModalCloseTimestamp]);
  ///////////////////////////////////////////////////////////////////////////////////////////

  const handlePick = (card) => {
    setSelectedCardId(card.id);
  };

  const handleSave = () => {
    if (!selectedCardId) {
      setToastMessage('Please select a card');
      setSnackbarOpen(true);
      return;
    }
    const card = (cards || []).find((c) => c.id === selectedCardId);
    if (!card) return;
    onSelect && onSelect(card);
    setOpen(false);
  };

  const handleCancel = () => {
    onCancel && onCancel();
    setOpen(false);
  };

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      aria-labelledby="card-select-title"
    >
      <Box sx={modalStyle}>
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={snackbarOpen}
          onClose={() => setSnackbarOpen(false)}
          autoHideDuration={1500}
          message={toastMessage}
        />

        <Typography id="card-select-title" variant="h6" className={classes.title}>
          Select Credit Card
        </Typography>

        {
          isLoading || cards === null ? (
            <Typography className={classes.empty}>Loading...</Typography>
          ) : cards.length === 0 ? (
            <Typography className={classes.empty}>No saved cards found.</Typography>
          ) : (
            <div className={classes.cardList}>
              {cards.map((card) => (
                <div
                  key={card.id}
                  className={`${classes.cardRow}${selectedCardId === card.id ? ` ${classes.cardRowSelected}` : ''}`}
                  onClick={() => handlePick(card)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handlePick(card); }}
                >
                  <div className={classes.cardLeft}>
                    <img
                      className={classes.cardIcon}
                      src={`/images/cards/${card.brand.toLowerCase()}.svg`}
                      alt={card.brand}
                    />
                    <div>
                      <div className={classes.cardNumber}>
                        {`${ucfirst(card.brand)} •••• ${card.last4}`}
                      </div>
                      <div className={classes.expiry}>
                        {`Expires ${moment(card.expYear + '-' + card.expMonth + '-01').format('MMM YYYY')}`}
                      </div>
                    </div>
                  </div>
                  <div className={classes.cardRight}>
                    {card.nickname ? (
                      <div className={classes.nickname}>{card.nickname}</div>
                    ) : (
                      <div className={classes.holderNameEmpty}>—</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        }

        {
          (!isLoading && cards !== null && !hideAdd) ? (
            <div className={classes.bottomActions}>
              <Button
                type="button"
                color="primary"
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAddCardModalOpen(true)}
                disabled={!user}
              >
                Add New Card
              </Button>
            </div>
          ) : null
        }

        <div className={classes.buttons}>
          <Button
            type="button"
            color="primary"
            variant="contained"
            onClick={handleSave}
            disabled={!selectedCardId}
          >
            Save
          </Button>
          <Button type="button" color="primary" variant="outlined" onClick={handleCancel}>
            Cancel
          </Button>
        </div>

        {
          user ? (
            <StripeCardModal
              open={addCardModalOpen}
              setOpen={setAddCardModalOpen}
              onSaved={onAddCardSaved}
              user={user}
              showToast={showToast}
            />
          ) : null
        }
      </Box>
    </Modal>
  );
};

export default CreditCardSelectModal;